import { memo, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { RiCloseLine } from "@remixicon/react"

import type { WidgetItem, WidgetType } from "@/types/dashboard"
import { useDashboardStore } from "@/stores/dashboard"
import { CalendarNavProvider } from "@/app/home/hooks/useCalendarNav"

import WidgetRenderer from "./WidgetRenderer"
import WidgetHeaderAction from "./WidgetHeaderAction"
import WidgetErrorBoundary from "./WidgetErrorBoundary"

// 타이틀을 카드 헤더에 노출하지 않는 위젯 (위젯 자체가 헤더 영역을 담당)
const WIDGETS_WITHOUT_TITLE = new Set<WidgetType>(["team-summary"])

// widgetType(hyphen) → i18n camelCase key
const toCamelKey = (s: string) => s.replace(/-(.)/g, (_, c) => c.toUpperCase())

// CalendarNavProvider로 감쌀 위젯 — 헤더 date nav와 본문이 같은 날짜 상태 공유
const WIDGETS_WITH_CALENDAR_NAV = new Set<WidgetType>(["calendar"])

interface DashboardWidgetCardProps {
  widget: WidgetItem
}

const DashboardWidgetCard = memo(function DashboardWidgetCard({
  widget,
}: DashboardWidgetCardProps) {
  const { t } = useTranslation()
  const isEditMode = useDashboardStore((s) => s.isEditMode)
  const removeWidget = useDashboardStore((s) => s.removeWidget)

  const withCalendarNav = WIDGETS_WITH_CALENDAR_NAV.has(widget.widgetType)
  const wrap = (children: ReactNode) =>
    withCalendarNav ? <CalendarNavProvider>{children}</CalendarNavProvider> : children

  return (
    <div className={`DashboardWidgetCard DashboardWidgetCard--${widget.widgetType}`}>
      {isEditMode && (
        <div className="DashboardWidgetCard__chip">
          <span className="DashboardWidgetCard__chip-label">
            {t(toCamelKey(widget.widgetType))}
          </span>
          <button
            className="DashboardWidgetCard__chip-btn"
            onClick={(e) => {
              e.stopPropagation()
              removeWidget(widget.id)
            }}
            title={t("button.delete")}>
            <RiCloseLine size={14} />
          </button>
        </div>
      )}

      {wrap(
        <>
          {!WIDGETS_WITHOUT_TITLE.has(widget.widgetType) && (
            <div className="DashboardWidgetCard__header">
              <h3 className="DashboardWidgetCard__title">{t(toCamelKey(widget.widgetType))}</h3>
              <WidgetErrorBoundary widgetTitle={widget.widgetType}>
                <WidgetHeaderAction widget={widget} />
              </WidgetErrorBoundary>
            </div>
          )}

          <div className="DashboardWidgetCard__content">
            <WidgetErrorBoundary widgetTitle={widget.widgetType}>
              <WidgetRenderer widget={widget} />
            </WidgetErrorBoundary>
          </div>
        </>,
      )}
    </div>
  )
})

export default DashboardWidgetCard
